import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TrackByFunction } from "@angular/core";
import { TranslocoService } from "@ngneat/transloco";
import { MatDialog } from "@angular/material/dialog";
import { Compbaser } from "../compbaser";
import { EnumSizeImageType } from "../dialog-get-asset/dialog-get-asset.component";
import { actionAddResources, actionGetAIImages, actionGetAIVideo } from "../../store/actions/user.actions";
import {
    AiTsrService,
    AppService,
    IAIImage,
    IAIUsageStats,
    LocalStoreService,
    SceneAddService,
    SceneService,
    StoreSelectorsService,
} from "../../services";
import { EnumWizardUpgradeToasts, IResource, IUser } from "../../interfaces";
import { ToastrService } from "ngx-toastr";
import { isHostLocal, TOAST_FASTER } from "../../common";
import { ofType } from "@ngrx/effects";
import { ActionsSubject } from "@ngrx/store";
import { take } from "rxjs/operators";
import { Lib } from "../../../libs/Lib";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

@Component({
    selector: "app-resources-ai",
    templateUrl: "./resources-ai.html",
    styleUrls: ["./resources-ai.scss"],
})
export class ResourcesAi extends Compbaser implements OnInit, OnDestroy {
    @Input() mediaTypeFilter: "image" | "video" | "both" = "both";
    @Input() miniMode = false;

    user: IUser;
    safeVideoUrl: SafeResourceUrl | null = null;
    videoUrl: string | null = null;
    filter = "";
    readonly filter1 = "a cool background of flowing colors kind of like a lava lamp which I will use as background for my Digital Signage presentation.";
    readonly filter2 = "a cool background of flowing colors kind of like a lava lamp in a repeat video loop which I will use as background for my Digital Signage presentation, no sound.";
    videoUploadReady = false;
    m_numSizeImageType = EnumSizeImageType;
    m_enumMediaTypeSelected = "1024x1024";

    loadingImages = false;
    aiImages: IAIImage[] = [];
    selectedMediaType: "image" | "video" = "image";
    selectedResolution: "low" | "high" = "low";

    // Usage stats
    imageUsage: IAIUsageStats | null = null;
    videoUsage: IAIUsageStats | null = null;

    // Video generation options
    selectedVideoModel: "veo3" | "veo3.1" | "veo3.1_fast" = "veo3.1_fast"; // Default to fastest/cheapest model
    selectedVideoDuration: 4 | 6 | 8 = 4; // Default to 4 seconds (shortest/cheapest)
    selectedVideoResolution: string = "1280:720";

    showMediaTypeSelector = true;

    constructor(
        public storeSelector: StoreSelectorsService,
        private toastr: ToastrService,
        private cd: ChangeDetectorRef,
        private appService: AppService,
        private actionsSubj: ActionsSubject,
        private translocoService: TranslocoService,
        public override dialog: MatDialog,
        private sceneService: SceneService,
        private sceneAddService: SceneAddService,
        private aiTsrService: AiTsrService, // Inject the new service
    ) {
        super(dialog);
        this.cod(
            this.storeSelector.getUser().subscribe((i_user: IUser | null) => {
                if (i_user) {
                    this.user = i_user;
                }
            }),
        );
    }

    ngOnInit() {
        // Check service state first to restore previous session
        const videoState = this.aiTsrService.getVideoState();
        const images = this.aiTsrService.getImages();

        // Restore component state from service
        this.aiImages = images;
        this.videoUrl = videoState.videoUrl;
        this.safeVideoUrl = videoState.safeVideoUrl;
        this.videoUploadReady = videoState.videoUploadReady;

        // Handle mediaTypeFilter input
        if (this.mediaTypeFilter === "image") {
            this.selectedMediaType = "image";
            this.showMediaTypeSelector = false;
            this.filter = this.filter1;
        } else if (this.mediaTypeFilter === "video") {
            this.selectedMediaType = "video";
            this.showMediaTypeSelector = false;
            this.filter = this.filter2;
        } else {
            // For 'both' mode, restore previous state if available
            this.showMediaTypeSelector = true;

            // If there's a video ready or being generated, switch to video mode
            if (videoState.videoUploadReady || videoState.polling || videoState.taskId) {
                this.selectedMediaType = "video";
                this.filter = this.filter2;
            }
            // If there are images ready, switch to image mode
            else if (images && images.length > 0) {
                this.selectedMediaType = "image";
                this.filter = this.filter1;
            }
            // Otherwise default to image mode
            else {
                this.selectedMediaType = "image";
                this.filter = this.filter1;
            }
        }

        // Subscribe to service observables for state
        this.subscribeToServiceState();

        // Trigger change detection to show restored state immediately
        this.cd.markForCheck();
    }

    /**
     * Subscribe to service state observables
     */
    private subscribeToServiceState(): void {
        // Subscribe to images from service
        this.cod(
            this.aiTsrService.aiImages$.subscribe((images: IAIImage[]) => {
                this.aiImages = images;
                this.cd.markForCheck();
            }),
        );

        // Subscribe to video state from service
        this.cod(
            this.aiTsrService.videoState$.subscribe((videoState) => {
                this.videoUrl = videoState.videoUrl;
                this.safeVideoUrl = videoState.safeVideoUrl;
                this.videoUploadReady = videoState.videoUploadReady;
                this.cd.markForCheck();
            }),
        );

        // Subscribe to loading state from service
        this.cod(
            this.aiTsrService.loading$.subscribe((loading: boolean) => {
                this.loadingImages = loading;
                this.cd.markForCheck();
            }),
        );

        // Subscribe to image usage stats from service
        this.cod(
            this.aiTsrService.imageUsage$.subscribe((usage: IAIUsageStats | null) => {
                this.imageUsage = usage;
                this.cd.markForCheck();
            }),
        );

        // Subscribe to video usage stats from service
        this.cod(
            this.aiTsrService.videoUsage$.subscribe((usage: IAIUsageStats | null) => {
                this.videoUsage = usage;
                this.cd.markForCheck();
            }),
        );
    }

    // UI Event Handlers
    onSelection(e: any): void {
        const value = e.value;
        if (value === "image" || value === "video") {
            this.clearFilterSelection();
            this.selectedMediaType = value;
            this.filter = (value === "image") ? this.filter1 : this.filter2;
            this.cd.markForCheck();
        }
    }

    onFilterSelection(e: any): void {
        const value = e?.value;
        if (typeof value === "string" && value.length > 1) {
            this.m_enumMediaTypeSelected = value;
            this.cd.markForCheck();
        }
    }

    // Sanitize prompt text
    private sanitizePrompt(text: string): string {
        if (!text) return "";
        let sanitized = text.replace(/[\r\n]+/g, " ");
        sanitized = sanitized.replace(/[^A-Za-z0-9\s]/g, "");
        sanitized = sanitized.replace(/\s+/g, " ");
        sanitized = sanitized.trim();
        return sanitized;
    }

    // Trigger AI generation (Image or Video) - delegate to service
    getImages(): void {
        if (this.loadingImages) return null;

        // Check if free user is trying to use premium features
        if (Lib.IsFreeAccount(this.user)) {
            // Allow free users ONLY for low-resolution images
            if (this.selectedMediaType === "video" || this.selectedResolution === "high") {
                this.appService.openWizardUpgradeDialog(EnumWizardUpgradeToasts.UPGRADE_TO_USE_FEATURE);
                return;
            }
            // Free users can proceed with low-resolution images (daily limits still apply on server)
        }

        if (!this.user) {
            this.toastr.error("User data not available. Please reload.", "Error");
            return;
        }

        if (Lib.IsAccessBlocked(this.user)) {
            this.toastr.error(this.translocoService.translate("account.screenDisruption", {}, this.translocoService.getActiveLang()), "[ Contact live chat ]");
            return;
        }

        const sanitizedPrompt = this.sanitizePrompt(this.filter);
        if (!sanitizedPrompt) {
            this.toastr.warning("Please enter a valid description for the AI (only letters, numbers, and spaces allowed).");
            return;
        }

        // Clear previous results and set loading via service
        this.aiTsrService.clearImages();
        this.aiTsrService.setLoading(true);
        this.cd.markForCheck();

        if (this.selectedMediaType === "image") {
            this.storeSelector.dispatch(actionGetAIImages({
                prompt: sanitizedPrompt,
                size: this.m_enumMediaTypeSelected,
                resolution: this.selectedResolution,
            }));
        } else {
            this.storeSelector.dispatch(actionGetAIVideo({
                prompt: sanitizedPrompt,
                model: this.selectedVideoModel,
                duration: this.selectedVideoDuration,
                resolution: this.selectedVideoResolution,
            }));
        }
    }

    // Upload image - delegate to service
    async uploadImageToResource(selectedImage: IAIImage): Promise<void> {
        await this.aiTsrService.uploadImageToResource(selectedImage, true);
    }

    // Upload video - delegate to service
    async uploadVideoToResource(): Promise<void> {
        await this.aiTsrService.uploadVideoToResource();
    }

    // Clear filter selection
    clearFilterSelection(): void {
        this.filter = "";
        this.aiTsrService.clearImages();
        this.cd.markForCheck();
    }

    // Add AI-generated image as scene background (miniMode)
    async addAsBackground(selectedImage: IAIImage): Promise<void> {
        // If already uploaded, reuse the resource
        if (selectedImage.uploadedResource) {
            this.sceneService.selectRootScene();
            this.sceneService.setBackgroundImage({
                resId: selectedImage.uploadedResource.resource_id,
                resType: selectedImage.uploadedResource.resource_type,
                showConfirmDialog: false,
            });
            selectedImage.backgroundSet = true;
            this.toastr.success("Added as scene background!", "", TOAST_FASTER);
            this.cd.markForCheck();
            return;
        }

        // First time - upload the image
        this.aiTsrService.setPendingMiniModeAction("background");

        // Subscribe ONCE to actionAddResources to handle the newly uploaded resource
        const subscription = this.actionsSubj
            .pipe(
                ofType(actionAddResources),
                take(1),
            )
            .subscribe((action: any) => {
                const resource: IResource = action.data;

                if (this.aiTsrService.getPendingMiniModeAction() === "background") {
                    selectedImage.uploadedResource = resource;
                    selectedImage.backgroundSet = true;

                    this.sceneService.selectRootScene();
                    this.sceneService.setBackgroundImage({
                        resId: resource.resource_id,
                        resType: resource.resource_type,
                        showConfirmDialog: false,
                    });

                    this.toastr.success("Added as scene background!", "", TOAST_FASTER);
                    this.aiTsrService.setPendingMiniModeAction(null);

                    selectedImage.uploading = false;
                    this.cd.markForCheck();
                }
            });

        await this.aiTsrService.uploadImageToResource(selectedImage);
    }

    // Add AI-generated image as resource block (miniMode)
    async addAsBlockImage(selectedImage: IAIImage): Promise<void> {
        // If already uploaded, reuse the resource
        if (selectedImage.uploadedResource) {
            this.sceneAddService.setBlockType("BLOCK_RESOURCES");

            const event = {
                id: selectedImage.uploadedResource.resource_id,
                type: selectedImage.uploadedResource.resource_type,
                name: selectedImage.uploadedResource.resource_name,
                mimeType: "",
                resource: selectedImage.uploadedResource,
            };

            this.sceneAddService.addBlock(event, null, true);

            this.toastr.success("Added to scene!", "", TOAST_FASTER);
            this.cd.markForCheck();
            return;
        }

        // First time - upload the image
        this.aiTsrService.setPendingMiniModeAction("block");

        const subscription = this.actionsSubj
            .pipe(
                ofType(actionAddResources),
                take(1),
            )
            .subscribe((action: any) => {
                const resource: IResource = action.data;

                if (this.aiTsrService.getPendingMiniModeAction() === "block") {
                    selectedImage.uploadedResource = resource;

                    this.sceneAddService.setBlockType("BLOCK_RESOURCES");

                    const event = {
                        id: resource.resource_id,
                        type: resource.resource_type,
                        name: resource.resource_name,
                        mimeType: "",
                        resource: resource,
                    };

                    this.sceneAddService.addBlock(event, null, true);

                    this.toastr.success("Added to scene!", "", TOAST_FASTER);
                    this.aiTsrService.setPendingMiniModeAction(null);

                    selectedImage.uploading = false;
                    this.cd.markForCheck();
                }
            });

        await this.aiTsrService.uploadImageToResource(selectedImage, true);
    }

    // For ngFor optimization
    trackByName: TrackByFunction<IAIImage> = (index: number, img: IAIImage): string => {
        return img.name || img.url || index.toString();
    };

    // Cleanup
    override destroy(): void {
        super.destroy();
        // Note: We don't clear the service state here so resources continue generating
        // even if user navigates away from this component
    }

    override ngOnDestroy(): void {
        this.destroy();
    }
}
